// components/dashboard/widgets/ProdutividadeDepartamento.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, Table, Progress, Tag } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from 'recharts';
import { 
  TrophyOutlined,
  TeamOutlined,
  FileTextOutlined,
  CalendarOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useDashboardData } from '../../../hooks/useDashboardData';

const ProdutividadeDepartamento = ({ endpoint, filtros, formato }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando produtividade departamental...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar produtividade departamental
        </p>
        <small style={{ color: '#cf1322' }}>
          {error?.message || String(error)}
        </small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: 40, 
        color: '#8c8c8c'
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
        <h4 style={{ color: '#8c8c8c', margin: 0 }}>
          Nenhum dado de produtividade disponível
        </h4>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0' }}>
          Aguardando dados dos departamentos.
        </p>
      </div>
    );
  }

  // Função para calcular índice de produtividade
  const calcularIndiceProdutividade = (item) => {
    const projetos = parseInt(item.total_projetos) || parseInt(item.projetos_orientados) || 0;
    const defesas = parseInt(item.total_defesas) || parseInt(item.defesas_realizadas) || 0;
    const aprovacoes = parseInt(item.total_aprovacoes) || parseInt(item.aprovadas) || 0;
    const professores = parseInt(item.total_professores) || parseInt(item.orientadores_ativos) || 1;
    
    // Índice baseado em projetos por orientador e taxa de sucesso
    const projetosPorOrientador = projetos / professores;
    const taxaSucesso = defesas > 0 ? (aprovacoes / defesas) * 100 : 0;
    
    return Math.round((projetosPorOrientador * 0.6 + taxaSucesso * 0.4) * 10) / 10;
  };

  // Função para classificar performance
  const getPerformanceDepartamento = (indice) => {
    if (indice >= 80) return 'excelente';
    if (indice >= 60) return 'bom';
    if (indice >= 40) return 'regular';
    if (indice >= 20) return 'baixo';
    return 'critico';
  };

  // Processar dados
  const dadosProcessados = dados
    .map((item) => {
      const departamento = item.departamento || item.nome_departamento || item.unidade || 'Departamento não informado';
      const departamentoAbrev = departamento.length > 25 ? departamento.substring(0, 25) + '...' : departamento;
      const indice = calcularIndiceProdutividade(item);
      
      return {
        departamento: departamento,
        departamento_abrev: departamentoAbrev,
        total_projetos: parseInt(item.total_projetos) || parseInt(item.projetos_orientados) || 0,
        total_defesas: parseInt(item.total_defesas) || parseInt(item.defesas_realizadas) || 0,
        total_aprovacoes: parseInt(item.total_aprovacoes) || parseInt(item.aprovadas) || 0,
        total_professores: parseInt(item.total_professores) || parseInt(item.orientadores_ativos) || 1,
        total_estudantes: parseInt(item.total_estudantes) || parseInt(item.estudantes_ativos) || 0,
        nota_media: parseFloat(item.nota_media) || parseFloat(item.media_avaliacoes) || 0,
        tempo_medio: parseInt(item.tempo_medio_dias) || parseInt(item.tempo_medio) || 0,
        indice_produtividade: indice,
        performance: getPerformanceDepartamento(indice),
        taxa_sucesso: item.total_defesas > 0 ? ((item.total_aprovacoes || 0) / item.total_defesas) * 100 : 0,
        projetos_por_orientador: (item.total_projetos || 0) / (item.total_professores || 1),
      };
    })
    .sort((a, b) => b.indice_produtividade - a.indice_produtividade);

  // Cores por performance
  const getCor = (performance) => {
    const cores = {
      excelente: '#52c41a',
      bom: '#1890ff',
      regular: '#fadb14',
      baixo: '#fa8c16',
      critico: '#f5222d',
    };
    return cores[performance] || '#8c8c8c';
  };

  // Ícones por performance
  const getIcone = (performance) => {
    if (performance === 'excelente') return <TrophyOutlined />;
    if (performance === 'bom') return <StarOutlined />;
    if (performance === 'regular') return <TeamOutlined />;
    if (performance === 'baixo') return <FileTextOutlined />;
    return <CalendarOutlined />;
  };

  // Estatísticas gerais
  const estatisticas = {
    totalDepartamentos: dadosProcessados.length,
    mediaIndice: dadosProcessados.length > 0 ? 
      dadosProcessados.reduce((sum, item) => sum + item.indice_produtividade, 0) / dadosProcessados.length : 0,
    excelentes: dadosProcessados.filter(item => item.performance === 'excelente').length,
    totalProjetos: dadosProcessados.reduce((sum, item) => sum + item.total_projetos, 0),
    totalProfessores: dadosProcessados.reduce((sum, item) => sum + item.total_professores, 0),
    melhorDepartamento: dadosProcessados.length > 0 ? dadosProcessados[0] : null,
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '250px',
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>
            🏢 {data.departamento}
          </p>
          <div style={{ fontSize: 12 }}>
            <p style={{ margin: '4px 0', color: '#1890ff' }}>
              📊 Índice: <strong>{data.indice_produtividade}</strong>
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              📚 Projetos: {data.total_projetos}
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              🎯 Defesas: {data.total_defesas}
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              ✅ Aprovações: {data.total_aprovacoes}
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              👨‍🏫 Professores: {data.total_professores}
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              📈 Taxa Sucesso: {data.taxa_sucesso.toFixed(1)}%
            </p>
            <div
              style={{
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: getCor(data.performance) + '20',
                color: getCor(data.performance),
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 'bold',
              }}
            >
              {data.performance.toUpperCase()}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
CustomTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.any,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        departamento: PropTypes.string,
        indice_produtividade: PropTypes.number,
        total_projetos: PropTypes.number,
        total_defesas: PropTypes.number,
        total_aprovacoes: PropTypes.number,
        total_professores: PropTypes.number,
        taxa_sucesso: PropTypes.number,
        performance: PropTypes.string
      })
    })
  )
};
  
  // Formato tabela
  if (formato === 'tabela') {
    const colunas = [
      {
        title: 'Departamento',
        dataIndex: 'departamento',
        key: 'departamento',
        width: '25%',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getCor(record.performance),
              }}
            />
            <span style={{ fontWeight: 'bold', fontSize: 12 }}>{text}</span>
          </div>
        ),
      },
      {
        title: 'Índice',
        dataIndex: 'indice_produtividade',
        key: 'indice',
        align: 'center',
        width: '10%',
        render: (value, record) => (
          <Tag color={getCor(record.performance)} style={{ fontWeight: 'bold' }}>
            {value}
          </Tag>
        ),
      },
      {
        title: 'Projetos',
        dataIndex: 'total_projetos',
        key: 'projetos',
        align: 'center',
        width: '10%',
      },
      {
        title: 'Defesas',
        dataIndex: 'total_defesas',
        key: 'defesas',
        align: 'center',
        width: '10%',
      },
      {
        title: 'Aprovações',
        dataIndex: 'total_aprovacoes',
        key: 'aprovacoes',
        align: 'center',
        width: '10%',
      },
      {
        title: 'Professores',
        dataIndex: 'total_professores',
        key: 'professores',
        align: 'center',
        width: '10%',
      },
      {
        title: 'Taxa Sucesso',
        dataIndex: 'taxa_sucesso',
        key: 'taxa',
        align: 'center',
        width: '15%',
        render: (value) => (
          <Progress
            percent={value}
            size="small"
            strokeColor={value >= 80 ? '#52c41a' : value >= 60 ? '#1890ff' : '#fa8c16'}
            format={(percent) => `${percent.toFixed(1)}%`}
          />
        ),
      },
      {
        title: 'Performance',
        dataIndex: 'performance',
        key: 'performance',
        align: 'center',
        width: '10%',
        render: (performance) => (
          <div style={{ color: getCor(performance), fontSize: 16 }}>
            {getIcone(performance)}
          </div>
        ),
      },
    ];

    return (
      <div style={{ height: 350 }}>
        {/* Estatísticas no topo */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 12,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.totalDepartamentos}</div>
            <div style={{ color: '#666' }}>Departamentos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {estatisticas.mediaIndice.toFixed(1)}
            </div>
            <div style={{ color: '#666' }}>Índice Médio</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>{estatisticas.excelentes}</div>
            <div style={{ color: '#666' }}>Excelentes</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#722ed1' }}>{estatisticas.totalProjetos}</div>
            <div style={{ color: '#666' }}>Total Projetos</div>
          </div>
        </div>

        <Table
          dataSource={dadosProcessados}
          columns={colunas}
          pagination={false}
          size="small"
          scroll={{ y: 250 }}
          rowKey="departamento"
        />
      </div>
    );
  }

  // Formato radar
  if (formato === 'radar') {
    const dadosRadar = dadosProcessados.slice(0, 5).map(item => ({
      departamento: item.departamento_abrev,
      projetos: Math.min((item.projetos_por_orientador / 10) * 100, 100),
      defesas: Math.min((item.total_defesas / 20) * 100, 100),
      sucesso: item.taxa_sucesso,
      qualidade: item.nota_media ? (item.nota_media / 20) * 100 : 0,
      eficiencia: item.tempo_medio > 0 ? Math.max(100 - (item.tempo_medio / 365) * 100, 0) : 50,
    }));

    return (
      <div style={{ height: 350 }}>
        {/* Estatísticas */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 12,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.totalDepartamentos}</div>
            <div style={{ color: '#666' }}>Departamentos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {estatisticas.mediaIndice.toFixed(1)}
            </div>
            <div style={{ color: '#666' }}>Índice Médio</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
              {estatisticas.melhorDepartamento ? estatisticas.melhorDepartamento.departamento_abrev : 'N/A'}
            </div>
            <div style={{ color: '#666' }}>Melhor Depto</div>
          </div>
        </div>

        <div style={{ width: '100%', height: 'calc(350px - 80px)' }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={dadosRadar}>
              <PolarGrid />
              <PolarAngleAxis dataKey="departamento" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar
                dataKey="projetos"
                stroke="#1890ff"
                fill="#1890ff"
                fillOpacity={0.3}
                name="Projetos por Orientador"
              />
              <Radar
                dataKey="sucesso"
                stroke="#52c41a"
                fill="#52c41a"
                fillOpacity={0.3}
                name="Taxa de Sucesso"
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Formato gráfico (padrão)
  return (
    <div style={{ height: 350 }}>
      {/* Estatísticas no topo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: 16,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          fontSize: 12,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.totalDepartamentos}</div>
          <div style={{ color: '#666' }}>Departamentos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
            {estatisticas.mediaIndice.toFixed(1)}
          </div>
          <div style={{ color: '#666' }}>Índice Médio</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>{estatisticas.excelentes}</div>
          <div style={{ color: '#666' }}>Excelentes</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#722ed1' }}>
            {(estatisticas.totalProjetos / estatisticas.totalProfessores).toFixed(1)}
          </div>
          <div style={{ color: '#666' }}>Proj/Prof</div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(350px - 110px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dadosProcessados.slice(0, 8)}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="departamento_abrev"
              tick={{ fontSize: 10 }}
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              label={{ value: 'Índice de Produtividade', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="indice_produtividade"
              name="Índice de Produtividade"
              radius={[4, 4, 0, 0]}
            >
              {dadosProcessados.slice(0, 8).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getCor(entry.performance)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda */}
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#52c41a' }} />
          Excelente (≥80)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1890ff' }} />
          Bom (60-79)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fadb14' }} />
          Regular (40-59)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          Crítico (&lt;20)
        </span>
      </div>
    </div>
  );
};

// PropTypes
ProdutividadeDepartamento.displayName = 'ProdutividadeDepartamento';
ProdutividadeDepartamento.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  formato: PropTypes.oneOf(['grafico', 'tabela', 'radar']),
};

ProdutividadeDepartamento.defaultProps = {
  filtros: {},
  formato: 'grafico',
};

export default ProdutividadeDepartamento;